// Something drawn at a board square. The hot path: a badge, a health bar, a
// gizmo, an action bar — anything whose position is the board's to decide.
//
// **The `style`-not-`sx` rule is encapsulated here rather than restated.**
// Emotion mints a permanent class per distinct value, so a component that
// re-projects every frame and writes its position through `sx` grows the
// document by hundreds of stylesheet rules a move. That is a standing repo
// rule and it was still being broken, so this component
// makes the breach impossible by construction: it does not take `sx` for
// position at all, and everything that can move per frame goes out in one
// `style` object.
//
// Nothing renders while the point is null — no renderer yet, or no square. An
// overlay parked off screen at `{-1000, -1000}` is still an element, still
// laid out, and still lying about where its subject is.

import { styled, useTheme } from "@mui/material";
import type { ReactNode, RefObject } from "react";

import type { BoardAnchorPort } from "./anchor";
import { type BoardPointMode, useBoardPoint } from "./useBoardPoint";

/**
 * Where the anchored box sits relative to the projected point.
 *
 * Presets rather than a free transform: three answers cover every caller in
 * the app, and a caller passing its own `transform` is a caller whose position
 * has stopped being the primitive's to decide.
 */
export type BoardAlign = "centre" | "above" | "below";

const ALIGN: Record<BoardAlign, string> = {
  centre: "translate(-50%, -50%)",
  above: "translate(-50%, -100%)",
  below: "translate(-50%, 0)"
};

export interface BoardAnchoredProps {
  board: RefObject<BoardAnchorPort | null>;
  /** Null draws nothing, which is how a caller turns the overlay off. */
  square: number | null;
  /** Centre of the square by default; `corner` is where badges hang. */
  mode?: BoardPointMode;
  /** Nudge in viewport pixels, applied after the projection. */
  offset?: { x: number; y: number };
  /** Default `centre`. */
  align?: BoardAlign;
  /**
   * Take clicks. **Off by default**, because the board is underneath and the
   * click is nearly always for it — the markers this replaces are
   * `pointerEvents: none` to a one, and that is asserted about them rather
   * than assumed.
   */
  interactive?: boolean;
  /**
   * Sorting *within* the board rung, for the depth-sorted case: everything on
   * the board plane sits under one orthographic camera at a fixed pitch, so
   * lower on screen is nearer the viewer and screen-y is the sort key.
   *
   * **Clamped below the next rung**, which is derived from the tokens rather
   * than written down again — an overlay that could climb past `chrome` is a
   * badge painted over the interface. The rung is a hundred tall, so a caller
   * sorting by screen-y scales its own y into that; anything bigger flattens
   * against the ceiling rather than escaping through it.
   */
  zOffset?: number;
  children: ReactNode;
  /** For the test hooks the markers already carry. */
  "data-testid"?: string;
}

// Static, so emotion mints it once. Everything per-instance is in `style`.
const Anchor = styled("div")({
  position: "fixed",
  pointerEvents: "none"
});

export function BoardAnchored({
  board,
  square,
  mode = "centre",
  offset,
  align = "centre",
  interactive = false,
  zOffset = 0,
  children,
  "data-testid": testId
}: BoardAnchoredProps) {
  const theme = useTheme();
  const point = useBoardPoint(board, square, mode);
  if (!point) return null;

  const { z } = theme.kit;
  const rung = z.chrome - z.boardOverlay;
  const within = Math.min(Math.max(Math.round(zOffset), 0), rung - 1);

  return (
    <Anchor
      data-testid={testId}
      // One object, one mechanism. `transform` and `zIndex` do not change per
      // frame, but splitting one element's positioning across `style` and `sx`
      // is how the rule above got broken in the first place.
      style={{
        left: point.x + (offset?.x ?? 0),
        top: point.y + (offset?.y ?? 0),
        transform: ALIGN[align],
        zIndex: z.boardOverlay + within,
        ...(interactive ? { pointerEvents: "auto" } : null)
      }}
    >
      {children}
    </Anchor>
  );
}
